import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {PresenterProps, withPresenter} from '../../../../Support/with_presenter';
import {CompareReportPresenter} from './CompareReportPresenter';
import {DateRange} from '../../../../Domain/DateRange';
import {Link} from 'react-router-dom';
import {Loader} from '../../../Components/Utils/Loader';
import {ErrorPlaceholder} from '../../../Components/Utils/ErrorPlaceholder';
import {Report} from '../../../../Models/Report';
import {CompareTable} from './Widgets/CompareTable';
import {SplitTable} from './Widgets/SplitTable';
import {AverageCurves} from './Widgets/AverageCurves';
import {Graphs} from './Widgets/Graphs';
import {Sidebar} from './Sidebar/Sidebar';
import {SplitStrategy} from '../../../../Business/calculateSplitTimes';
import {CompareReportContent} from './CompareReportContent';
import {FeatureFlags} from '../../../../FeatureFlags';

interface OwnProps {
    snippetCollectionIds: number[];
    userIds: number[];
    crewIds: number[];
    dateRange: DateRange | null;
    report?: Report;
}

const CompareReportComponent: FunctionComponent<OwnProps & PresenterProps<CompareReportPresenter>> = observer(
    ({report, presenter}) => {
        if (presenter.snippetsResult.status === 'loading') {
            return (
                <div className="container">
                    <div className="row">
                        <div className="col-12 d-flex flex-row justify-content-start">
                            <Link to="/reports" className="btn btn-back-arrow">
                                <i className="bi bi-arrow-left"></i>
                            </Link>
                            <h1>Report</h1>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <Loader className="mt-5" text={presenter.snippetsResult.message || null} />
                        </div>
                    </div>
                </div>
            );
        }

        if (presenter.snippetsResult.status === 'error') {
            return (
                <div className="container">
                    <div className="row">
                        <div className="col-12 d-flex flex-row justify-content-start">
                            <Link to="/reports" className="btn btn-back-arrow">
                                <i className="bi bi-arrow-left"></i>
                            </Link>
                            <h1>Report</h1>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <ErrorPlaceholder large className="mt-5" />
                        </div>
                    </div>
                </div>
            );
        }

        if (presenter.snippetsResult.data.length === 0) {
            return (
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <div className="row">
                                <div className="col-12 d-flex flex-row justify-content-start">
                                    <Link to="/reports" className="btn btn-back-arrow">
                                        <i className="bi bi-arrow-left"></i>
                                    </Link>
                                    <h1>Report</h1>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12 col-md-9">
                                    <div className="col-12">
                                        <div className="card">
                                            <div className="card-body">
                                                <span className="workoutlist__empty">
                                                    <span className="workoutlist__empty_image" />
                                                    <span className="workoutlist__empty_text">
                                                        <p>Empty report</p>
                                                        <small>
                                                            Start building your report by adding your own{' '}
                                                            <Link to="/workouts">workouts</Link> or selected ranges from
                                                            them.
                                                            {FeatureFlags.crews && (
                                                                <>
                                                                    {' '}
                                                                    You can also include workouts from your{' '}
                                                                    <Link to="/crews">crewmates</Link>.
                                                                </>
                                                            )}
                                                            <>
                                                                {' '}
                                                                Use the workout sidebar to add a full workout or a
                                                                specific range.
                                                            </>
                                                        </small>
                                                    </span>
                                                    <Link to="/workouts" className="btn-primary btn">
                                                        Browse Workouts
                                                    </Link>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-3">
                                    <Sidebar
                                        report={report}
                                        onDelete={presenter.delete}
                                        deleting={presenter.deleting}
                                        snippets={presenter.snippetsResult.data}
                                        splitStrategy={presenter.splitStrategy}
                                        onChangeSplitStrategy={(splitStrategy: SplitStrategy) =>
                                            presenter.onChangeSplitStrategy(splitStrategy)
                                        }
                                        selectedSnippetIds={presenter.selectedSnippets.map((snippet) => snippet.id)}
                                        onChangeSelectedSnippetIds={(snippetIds) => {
                                            presenter.onChangeSelectedSnippetIds(snippetIds);
                                        }}
                                        onDeleteSnippet={(selectedSnippetId) => presenter.removeSnippet(selectedSnippetId)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (presenter.selectedSnippets.length === 0) {
            return (
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <div className="row">
                                <div className="col-12 d-flex flex-row justify-content-start">
                                    <Link to="/reports" className="btn btn-back-arrow">
                                        <i className="bi bi-arrow-left"></i>
                                    </Link>
                                    {!presenter.isEditingName && (
                                        <div className="d-flex flex-row justify-content-between w-100">
                                            <h1>
                                                {presenter.name === '' ? 'Report' : presenter.name}
                                                {!presenter.isLocked && (
                                                    <button
                                                        className="btn btn-link"
                                                        onClick={() => presenter.startEditName()}
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </button>
                                                )}
                                            </h1>
                                        </div>
                                    )}
                                    {presenter.isEditingName && (
                                        <h1>
                                            <form
                                                className="d-flex flex-row justify-content-start"
                                                onSubmit={(e) => {
                                                    e.preventDefault();
                                                    if (!presenter.isSaving) {
                                                        presenter.submit();
                                                    }
                                                }}
                                            >
                                                <input
                                                    autoFocus
                                                    disabled={presenter.isSaving}
                                                    type="text"
                                                    className="form-control"
                                                    value={presenter.name}
                                                    onKeyUp={(e) => {
                                                        if (e.key === 'Escape') {
                                                            presenter.stopEditName();
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        presenter.updateName(e.target.value);
                                                    }}
                                                />
                                                <button type="submit" className="btn btn-link">
                                                    <i className="bi bi-check"></i>
                                                </button>
                                            </form>
                                        </h1>
                                    )}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12 col-md-9">
                                    <div className="col-12">
                                        <div className="card">
                                            <div className="card-body">
                                                <span className="workoutlist__empty">
                                                    <span className="workoutlist__empty_image" />
                                                    <span className="workoutlist__empty_text">
                                                        <p>No workouts selected</p>
                                                        <small>Select some workouts for this report.</small>
                                                    </span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-3">
                                    <Sidebar
                                        report={report}
                                        onDelete={presenter.delete}
                                        deleting={presenter.deleting}
                                        snippets={presenter.snippetsResult.data}
                                        splitStrategy={presenter.splitStrategy}
                                        onChangeSplitStrategy={(splitStrategy: SplitStrategy) =>
                                            presenter.onChangeSplitStrategy(splitStrategy)
                                        }
                                        selectedSnippetIds={presenter.selectedSnippets.map((snippet) => snippet.id)}
                                        onChangeSelectedSnippetIds={(snippetIds) => {
                                            presenter.onChangeSelectedSnippetIds(snippetIds);
                                        }}
                                        onDeleteSnippet={(selectedSnippetId) => presenter.removeSnippet(selectedSnippetId)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <div className="row">
                            <div className="col-12 d-flex flex-row justify-content-start">
                                <Link to="/reports" className="btn btn-back-arrow">
                                    <i className="bi bi-arrow-left"></i>
                                </Link>
                                {!presenter.isEditingName && (
                                    <div className="d-flex flex-row justify-content-between w-100">
                                        <h1>
                                            {presenter.name === '' ? 'Report' : presenter.name}
                                            {!presenter.isLocked && (
                                                <button
                                                    className="btn btn-link"
                                                    onClick={() => presenter.startEditName()}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                            )}
                                        </h1>
                                    </div>
                                )}
                                {presenter.isEditingName && (
                                    <h1>
                                        <form
                                            className="d-flex flex-row justify-content-start"
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                if (!presenter.isSaving) {
                                                    presenter.submit();
                                                }
                                            }}
                                        >
                                            <input
                                                autoFocus
                                                disabled={presenter.isSaving}
                                                type="text"
                                                className="form-control"
                                                value={presenter.name}
                                                onKeyUp={(e) => {
                                                    if (e.key === 'Escape') {
                                                        presenter.stopEditName();
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    presenter.updateName(e.target.value);
                                                }}
                                            />
                                            <button type="submit" className="btn btn-link">
                                                <i className="bi bi-check"></i>
                                            </button>
                                        </form>
                                    </h1>
                                )}
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12 col-md-9">
                                <div className="row mb-3">
                                    <div className="col-12">
                                        <div className="form-group">
                                            <textarea
                                                className="form-control"
                                                id="description"
                                                placeholder="Comment area for your report..."
                                                value={presenter.description}
                                                onChange={(e) => presenter.updateDescription(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <CompareReportContent
                                    key={presenter.selectedSnippets.map((snippet) => snippet.id).join('-')}
                                    snippets={presenter.selectedSnippets}
                                    splitStrategy={presenter.splitStrategy}
                                />
                            </div>
                            <div className="col-12 col-md-3">
                                <Sidebar
                                    report={report}
                                    onDelete={presenter.delete}
                                    deleting={presenter.deleting}
                                    splitStrategy={presenter.splitStrategy}
                                    onChangeSplitStrategy={(splitStrategy: SplitStrategy) =>
                                        presenter.onChangeSplitStrategy(splitStrategy)
                                    }
                                    snippets={presenter.snippetsResult.data}
                                    selectedSnippetIds={presenter.selectedSnippets.map((snippet) => snippet.id)}
                                    onChangeSelectedSnippetIds={(snippetIds) => {
                                        presenter.onChangeSelectedSnippetIds(snippetIds);
                                    }}
                                    onDeleteSnippet={(selectedSnippetId) => presenter.removeSnippet(selectedSnippetId)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

export const CompareReport = withPresenter<CompareReportPresenter, OwnProps>(
    (props, component) =>
        new CompareReportPresenter(
            props.report || null,
            props.report?.id || null,
            props.snippetCollectionIds,
            props.userIds,
            props.crewIds,
            props.dateRange,
            component.network.snippets,
            component.network.report,
        ),
    CompareReportComponent,
);
